import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import config from '../config';
import './GlobalSearch.css';

const GlobalSearch = ({ isOpen, onClose, onNavigate, user }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({
    projects: [],
    templates: [],
    conversations: [],
    files: [],
  });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Perform search via BFF endpoint
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setResults({ projects: [], templates: [], conversations: [], files: [] });
      return;
    }

    setIsLoading(true);
    try {
      const baseUrl = config.bffUrl || window.location.origin;
      const userEmail = user?.email || user?.sub || 'demo-user';
      
      const response = await fetch(
        `${baseUrl}/api/search?q=${encodeURIComponent(searchQuery)}&category=${selectedCategory}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': userEmail,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setResults(data.results || { projects: [], templates: [], conversations: [], files: [] });
    } catch (error) {
      console.error('Search error:', error);
      setResults({ projects: [], templates: [], conversations: [], files: [] });
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, user]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, performSearch]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    const allResults = getAllResults();
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && allResults[selectedIndex]) {
      e.preventDefault();
      handleSelectResult(allResults[selectedIndex]);
    }
  };

  const getAllResults = () => {
    const all = [];
    if (selectedCategory === 'all' || selectedCategory === 'projects') {
      all.push(...results.projects.map(p => ({ ...p, type: 'project' })));
    }
    if (selectedCategory === 'all' || selectedCategory === 'templates') {
      all.push(...results.templates.map(t => ({ ...t, type: 'template' })));
    }
    if (selectedCategory === 'all' || selectedCategory === 'conversations') {
      all.push(...results.conversations.map(c => ({ ...c, type: 'conversation' })));
    }
    if (selectedCategory === 'all' || selectedCategory === 'files') {
      all.push(...results.files.map(f => ({ ...f, type: 'file' })));
    }
    return all;
  };

  const handleSelectResult = (result) => {
    if (!result) return;

    switch (result.type) {
      case 'project':
        if (onNavigate) {
          onNavigate('/chat', { project: result });
        }
        break;
      case 'template':
        if (onNavigate) {
          onNavigate('/chat', { template: result });
        }
        break;
      case 'conversation':
        if (onNavigate) {
          onNavigate('/chat', { conversationId: result.conversationId });
        }
        break;
      case 'file':
        if (onNavigate) {
          onNavigate('/chat', { file: result });
        }
        break;
      default:
        break;
    }
    
    onClose();
    setQuery('');
    setResults({ projects: [], templates: [], conversations: [], files: [] });
  };

  const getResultIcon = (type) => {
    switch (type) {
      case 'project':
        return 'folder';
      case 'template':
        return 'layout';
      case 'conversation':
        return 'message-square';
      case 'file':
        return 'file';
      default:
        return 'search';
    }
  };

  const getCategoryCount = (category) => {
    return results[category]?.length || 0;
  };

  if (!isOpen) return null;

  const allResults = getAllResults();
  const hasResults = allResults.length > 0;

  return (
    <div className="global-search-overlay">
      <div className="global-search-modal" ref={modalRef}>
        {/* Search Input */}
        <div className="global-search-header">
          <Icon name="search" size={20} className="search-header-icon" />
          <input
            ref={inputRef}
            type="text"
            className="global-search-input"
            placeholder="Search projects, templates, conversations..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {isLoading && (
            <div className="search-loading-spinner">
              <Icon name="loader" size={16} className="spinning" />
            </div>
          )}
          <kbd className="search-kbd">ESC</kbd>
        </div>

        {/* Category Tabs */}
        <div className="global-search-categories">
          <button
            className={`category-tab ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
          >
            All
            {query && <span className="category-count">{allResults.length}</span>}
          </button>
          <button
            className={`category-tab ${selectedCategory === 'projects' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('projects')}
          >
            <Icon name="folder" size={14} />
            Projects
            {query && <span className="category-count">{getCategoryCount('projects')}</span>}
          </button>
          <button
            className={`category-tab ${selectedCategory === 'templates' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('templates')}
          >
            <Icon name="layout" size={14} />
            Templates
            {query && <span className="category-count">{getCategoryCount('templates')}</span>}
          </button>
          <button
            className={`category-tab ${selectedCategory === 'conversations' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('conversations')}
          >
            <Icon name="message-square" size={14} />
            Chats
            {query && <span className="category-count">{getCategoryCount('conversations')}</span>}
          </button>
          <button
            className={`category-tab ${selectedCategory === 'files' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('files')}
          >
            <Icon name="file" size={14} />
            Files
            {query && <span className="category-count">{getCategoryCount('files')}</span>}
          </button>
        </div>

        {/* Results */}
        <div className="global-search-results">
          {!query && (
            <div className="search-empty-state">
              <Icon name="search" size={48} className="empty-icon" />
              <p className="empty-title">Start typing to search</p>
              <p className="empty-subtitle">Search across projects, templates, conversations, and files</p>
              <div className="search-shortcuts">
                <div className="shortcut-item">
                  <kbd>↑</kbd> <kbd>↓</kbd> Navigate
                </div>
                <div className="shortcut-item">
                  <kbd>Enter</kbd> Select
                </div>
                <div className="shortcut-item">
                  <kbd>ESC</kbd> Close
                </div>
              </div>
            </div>
          )}

          {query && !isLoading && !hasResults && (
            <div className="search-empty-state">
              <Icon name="search-x" size={48} className="empty-icon" />
              <p className="empty-title">No results found</p>
              <p className="empty-subtitle">Try searching for something else</p>
            </div>
          )}

          {query && hasResults && (
            <div className="search-results-list">
              {allResults.map((result, index) => (
                <button
                  key={`${result.type}-${result._id || result.id || index}`}
                  className={`search-result-item ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => handleSelectResult(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="result-icon">
                    <Icon name={getResultIcon(result.type)} size={18} />
                  </div>
                  <div className="result-content">
                    <div className="result-title">
                      {result.name || result.title || 'Untitled'}
                    </div>
                    {result.description && (
                      <div className="result-description">{result.description}</div>
                    )}
                    {result.snippet && (
                      <div className="result-snippet">{result.snippet}</div>
                    )}
                  </div>
                  <div className="result-meta">
                    <span className="result-type">{result.type}</span>
                    {result.updatedAt && (
                      <span className="result-date">
                        {new Date(result.updatedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="global-search-footer">
          <div className="search-tips">
            <Icon name="lightbulb" size={14} />
            <span>Tip: Use ⌘K to quickly open search from anywhere</span>
          </div>
        </div>
      </div>
    </div>
  );
};

GlobalSearch.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
  user: PropTypes.object,
};

export default GlobalSearch;
